import { ChangeEvent, Ref, useRef, useState } from 'react';
import styles from './DebtCard.module.scss';
import MeritoSelectField from '../../pages/Underwriting/components/selectfields/MeritoSelectField';
import MeritoButton from '../common/MeritoButtons/MeritoButtons';
// same trash icon as the bank statements account cards
import DeleteIcon from '../../assets/images/bankStatements/delete-account.svg';
import { DebtCardData, DebtDocument, debtDocumentStatus, debtDocumentType, debtErrorMessage, debtType } from '../../interfaces/interfaces';
import { DebtApi } from './debtApi';
import {
    debtTypeOptions,
    getClassificationTags,
    getDebtTypeLabel,
    getDocumentError,
    isDraftDebt,
} from './debtHelpers';
import {
    getSanctionLetterDebtType,
    hasRepaymentScheduleDocument,
    isProductMismatch,
    isRepaymentScheduleMissing,
    isSanctionLetterProcessed,
} from './debtValidation';
import ErrorBanner from './ErrorBanner';
import DebtFileRow from './DebtFileRow';
import DebtDetailsFields from './DebtDetailsFields';
import DebtConfirmationModal, { PendingConfirmation } from './DebtConfirmationModal';

type Props = {
    containerRef?: Ref<HTMLDivElement>;
    // a real debt, or a draft before its sanction letter is uploaded
    debt: DebtCardData;
    // replaces this card's debt with the server's latest copy (or a fresh draft)
    onChange: (debt: DebtCardData) => void;
    // removes this card from the list; used when its debt is deleted
    onRemove: () => void;
    // a lone blank card has nothing to delete, so it gets no trash button
    isOnlyCard: boolean;
    api: DebtApi;
    // the parent form was submitted with errors: show missing details even on
    // fields the user never touched, like the company details inputs do
    showErrors?: boolean;
    // Next failed with no sanction letter uploaded on any card; decided by the
    // list, since only it can see the other cards
    showUploadError?: boolean;
};

// One lender's card. Holds the card's state and its actions (upload, delete,
// change type); the file rows, error boxes, balance/date inputs and the
// confirmation popup are their own components.
const DebtCard = ({ containerRef, debt, onChange, onRemove, isOnlyCard, api, showErrors = false, showUploadError = false }: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const scheduleInputRef = useRef<HTMLInputElement>(null);
    // A file on its way to the server, shown as a row straight away (same idea as
    // the bank statements upload queue). Kept here, not in the debt: the debt is
    // the server's copy and gets replaced by every reply and socket event, and
    // this must never reach the form or the localStorage draft.
    const [pendingUpload, setPendingUpload] = useState<File | null>(null);
    const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);
    const [deletingDebt, setDeletingDebt] = useState(false);
    const [changingDebtType, setChangingDebtType] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    // action waiting on the user's answer in the confirmation popup
    const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);

    const documents = debt?.documents || [];

    // a blank card has no debtId: nothing is on the server yet
    const isDraft = isDraftDebt(debt);
    // the backend refuses to delete a debt while any of its files is mid-OCR
    const isAnyDocumentProcessing = documents.some(
        (document) => document.status === debtDocumentStatus.processing
    );
    // an upload in flight counts too: a delete now would shift the list and let
    // the upload's reply land on whichever card took this one's place
    const isBusy = deletingDocumentId != null || deletingDebt || changingDebtType || pendingUpload != null;

    // true for a combined PDF and for a separately uploaded repayment schedule
    const hasRepaymentSchedule = hasRepaymentScheduleDocument(debt);

    // a schedule uploaded as its own file; removing the sanction letter takes it too
    const hasSeparateSchedule = documents.some(
        (document) => document.jobType === debtDocumentType.repaymentSchedule
    );

    // the details inputs appear once OCR has read a sanction letter; same rule the
    // page validation uses to require them
    const hasProcessedSanctionLetter = isSanctionLetterProcessed(debt);

    // Same rules the page validation and the checklist use (debtValidation.ts):
    // a letter for the wrong loan type blocks the final submit, a missing term
    // loan schedule blocks Next.
    const productDebtType = getSanctionLetterDebtType(debt);
    const hasProductMismatch = isProductMismatch(debt);
    const isScheduleMissing = isRepaymentScheduleMissing(debt);

    const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // reset so the same file can be picked again after a failure
        event.target.value = '';
        if (!file) return;

        setPendingUpload(file);
        setErrorMessage('');
        try {
            const createdDebt = await api.uploadSanctionLetter({
                file,
                debtType: debt?.debtType || debtType.term,
            });
            // the draft card becomes the real debt: debtId + documents now live in the
            // list. Cleared in the same tick, so the real row replaces the pending one
            // in one render and the spinner just carries on.
            onChange(createdDebt);
            setPendingUpload(null);
        } catch {
            setPendingUpload(null);
            setErrorMessage(debtErrorMessage.sanctionLetterUpload);
        }
    };

    const handleScheduleSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // reset so the same file can be picked again after a failure
        event.target.value = '';
        // a schedule attaches to an existing debt, so a draft can't take one
        if (!file || isDraftDebt(debt)) return;

        setPendingUpload(file);
        setErrorMessage('');
        try {
            // the returned debt carries the new file in PROCESSING; the socket brings the result
            const updatedDebt = await api.uploadRepaymentSchedule(debt.debtId, file);
            onChange(updatedDebt);
            setPendingUpload(null);
        } catch {
            setPendingUpload(null);
            setErrorMessage(debtErrorMessage.repaymentScheduleUpload);
        }
    };

    const handleDeleteDebt = async () => {
        // drafts are removed directly by handleTrashClick, never through the API
        if (isDraftDebt(debt)) return;
        setDeletingDebt(true);
        setErrorMessage('');
        try {
            await api.deleteDebt(debt.debtId);
            onRemove();
        } catch {
            setErrorMessage(debtErrorMessage.debtRemove);
        } finally {
            setDeletingDebt(false);
        }
    };

    // Keeps the debt, its files and details: only what the loan needs changes
    // (a term loan asks for a start date and repayment schedule, revolving doesn't).
    const handleChangeDebtType = async (newDebtType: debtType) => {
        // a draft changes type directly in the select, with nothing on the server
        if (isDraftDebt(debt)) return;
        setChangingDebtType(true);
        setErrorMessage('');
        try {
            const updatedDebt = await api.updateDebtDetails(debt.debtId, { debtType: newDebtType });
            onChange(updatedDebt);
        } catch {
            setErrorMessage(debtErrorMessage.debtTypeChange);
        } finally {
            setChangingDebtType(false);
        }
    };

    const handleDeleteDocument = async (document: DebtDocument) => {
        // a draft has no documents to delete
        if (isDraftDebt(debt)) return;
        setDeletingDocumentId(document.documentId);
        setErrorMessage('');
        try {
            if (document.jobType === debtDocumentType.sanctionLetter) {
                // the backend only allows dropping a sanction letter by deleting the debt
                await api.deleteDebt(debt.debtId);
                onRemove();
                return;
            }
            const updatedDebt = await api.deleteDebtDocument(debt.debtId, document.documentId);
            onChange(updatedDebt);
        } catch {
            setErrorMessage(debtErrorMessage.fileRemove);
        } finally {
            setDeletingDocumentId(null);
        }
    };

    const handleTrashClick = () => {
        // nothing on the server and nothing to re-enter, so no warning needed
        if (isDraft) {
            onRemove();
            return;
        }
        setPendingConfirmation({ kind: 'deleteDebt' });
    };

    const handleRemoveDocumentClick = (document: DebtDocument) => {
        // Removing the sanction letter wipes the whole debt, so ask first when there
        // is a schedule to lose. A schedule file on its own is removed straight away.
        if (document.jobType === debtDocumentType.sanctionLetter && hasRepaymentSchedule) {
            setPendingConfirmation({ kind: 'removeSanctionLetter', document });
        } else {
            handleDeleteDocument(document);
        }
    };

    const handleConfirm = async () => {
        if (!pendingConfirmation) return;
        // failures show on the card; the popup closes either way
        if (pendingConfirmation.kind === 'deleteDebt') {
            await handleDeleteDebt();
        } else if (pendingConfirmation.kind === 'changeDebtType') {
            await handleChangeDebtType(pendingConfirmation.newDebtType);
        } else {
            await handleDeleteDocument(pendingConfirmation.document);
        }
        setPendingConfirmation(null);
    };

    return (
        <div ref={containerRef} className={styles.cardContainer}>
            <div className={styles.headerRow}>
                <div className={styles.header}>
                    <div className={styles.title}>{debt?.lender || 'New Lender'}</div>
                    {debt?.sanctionLetterNo ? (
                        <div className={styles.subtitle}>Sanction letter: {debt.sanctionLetterNo}</div>
                    ) : null}
                </div>
                {/* Keep one loan card in the flow; its documents can still be removed to start fresh. */}
                {!isOnlyCard ? (
                    <span title={isAnyDocumentProcessing ? 'Processing' : 'Delete debt'}>
                        <MeritoButton
                            type="destructive-ghost"
                            size="md2"
                            onlyIcon
                            className={styles.deleteDebtButton}
                            // icon-only, so screen readers need a name
                            ariaLabel="Delete debt"
                            icon={<img src={DeleteIcon} alt="" />}
                            disabled={isAnyDocumentProcessing || isBusy}
                            onClick={handleTrashClick}
                        />
                    </span>
                ) : null}
            </div>
            {documents.map((document) => {
                const documentError = getDocumentError(document);
                return documentError ? (
                    <ErrorBanner key={document.documentId} title={documentError.title} action={documentError.action} />
                ) : null;
            })}
            {/* hidden while the upload is in flight; once it lands the card has a
                debtId, validation passes and this goes away on its own */}
            {showUploadError && !pendingUpload ? <ErrorBanner title="Upload Sanction Letter" /> : null}
            {hasProductMismatch ? (
                <ErrorBanner
                    title={`This sanction letter looks like a ${getDebtTypeLabel(productDebtType)} loan`}
                    action={`Remove it and upload the sanction letter for this ${getDebtTypeLabel(debt.debtType)} loan.`}
                />
            ) : null}
            {isScheduleMissing ? <ErrorBanner title="Upload Repayment Schedule" /> : null}
            <div className={styles.fieldsContainer}>
                <MeritoSelectField
                    options={debtTypeOptions}
                    optionLabelKey="label"
                    label="Debt Type"
                    size="sm"
                    isSearchable={false}
                    isClearable={false}
                    // the type stays put while a file is still being read
                    isDisabled={isAnyDocumentProcessing}
                    error={null}
                    touched={false}
                    value={debtTypeOptions.find((option) => option.value === debt?.debtType)}
                    onChange={(event) => {
                        // MeritoSelectField hands back the whole option as target.value
                        const option = event.target.value as unknown as { value: debtType } | null;
                        if (!option?.value || option.value === debt?.debtType) return;
                        // once something is uploaded, the change goes to the server, so ask first
                        if (!isDraft) {
                            setPendingConfirmation({
                                kind: 'changeDebtType',
                                fromDebtType: debt.debtType,
                                newDebtType: option.value,
                            });
                            return;
                        }
                        onChange({ ...debt, debtType: option.value });
                    }}
                />

                {documents.length || pendingUpload ? (
                    <div className={styles.filesContainer}>
                        {documents.map((document) => (
                            <DebtFileRow
                                key={document.documentId}
                                fileName={document.originalFileName}
                                fileKey={document.fileKey}
                                tags={getClassificationTags(document, isScheduleMissing)}
                                isProcessing={document.status === debtDocumentStatus.processing}
                                onViewError={setErrorMessage}
                                remove={{
                                    onClick: () => handleRemoveDocumentClick(document),
                                    // no file comes off a debt while any of its files is mid-OCR
                                    disabled: isBusy || isAnyDocumentProcessing,
                                    loading: deletingDocumentId === document.documentId,
                                }}
                            />
                        ))}

                        {/* the in-flight file: local name, viewable from the local copy, no ×
                            since nothing exists on the server to delete yet */}
                        {pendingUpload ? (
                            <DebtFileRow
                                fileName={pendingUpload.name}
                                file={pendingUpload}
                                isProcessing={true}
                                onViewError={setErrorMessage}
                            />
                        ) : null}

                        {isScheduleMissing && !pendingUpload ? (
                            <div className={styles.scheduleUpload}>
                                <input
                                    ref={scheduleInputRef}
                                    type="file"
                                    accept="application/pdf"
                                    hidden
                                    onChange={handleScheduleSelected}
                                />
                                <MeritoButton
                                    btnText="Upload Repayment Schedule"
                                    type="secondary-2"
                                    size="md"
                                    onClick={() => scheduleInputRef.current?.click()}
                                />
                            </div>
                        ) : null}

                        {hasProcessedSanctionLetter ? (
                            <DebtDetailsFields
                                debt={debt}
                                api={api}
                                onChange={onChange}
                                showErrors={showErrors}
                                onErrorMessage={setErrorMessage}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.actionContainer}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            hidden
                            onChange={handleFileSelected}
                        />
                        <MeritoButton
                            btnText="Upload Sanction Letter"
                            type="secondary-2"
                            size="md"
                            onClick={() => fileInputRef.current?.click()}
                        />
                    </div>
                )}

                {errorMessage ? <div className={styles.errorText}>{errorMessage}</div> : null}
            </div>

            <DebtConfirmationModal
                pending={pendingConfirmation}
                hasSeparateSchedule={hasSeparateSchedule}
                isBusy={isBusy}
                onCancel={() => setPendingConfirmation(null)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default DebtCard;
